import { ChangeDetectionStrategy, ChangeDetectorRef, Component, Inject, OnInit, ViewEncapsulation } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { Observable } from 'rxjs';
import { isNil } from 'lodash';
import { IActivity, ISection } from 'typings/doenkids/doenkids';
import { IAiAdjustmentRequest, IAiAdjustmentResponse } from 'typings/api-activity';
import { AiAdjustmentService } from 'src/api/activity/ai-adjustment/ai-adjustment.service';
import { BreakpointsProvider } from 'src/providers/breakpoints.provider';
import {
  buildComparisonRows,
  describeAdjustmentFailure,
  EAiAdjustmentFailure,
  EAiComparisonRowKind,
  IAiAdjustmentFailureDetails,
  IAiComparisonRow,
} from '../ai-adjustment.model';

/** What the dialog is showing right now. */
export enum EAiComparisonState {
  GENERATING = 'generating',
  COMPARING = 'comparing',
  FAILED = 'failed',
}

/** Which column the narrow layout is showing. */
export type TAiComparisonTab = 'old' | 'new';

/** What the dialog needs to run a request and render the old column. */
export interface IAiComparisonDialogData {
  activity: IActivity;
  sections: ISection[];
  request: IAiAdjustmentRequest;
}

/** How the dialog closed, so the caller knows what to do next. */
export interface IAiComparisonDialogResult {
  decision: 'accept-update' | 'accept-copy' | 'discard' | 'failed';
  proposal?: IAiAdjustmentResponse;
}

/**
 * The compare-and-decide dialog.
 *
 * It opens straight into its generating state and fires the request itself,
 * rather than waiting for a proposal to be handed to it. That is deliberate:
 * the prototype shows the comparison filling in live, and until the response
 * lands there is no way to know whether this attempt will succeed or be
 * refused, so one dialog owns the whole outcome and swaps its own content.
 *
 * Not dismissable while generating, because the request is already costing
 * tokens and a closed dialog would leave the proposal stranded.
 */
@Component({
  selector: 'app-ai-adjustment-comparison-dialog',
  templateUrl: './ai-adjustment-comparison-dialog.component.html',
  styleUrls: ['./ai-adjustment-comparison-dialog.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None,
  standalone: false,
})
export class AiAdjustmentComparisonDialogComponent implements OnInit {
  public readonly EAiComparisonState = EAiComparisonState;

  public readonly EAiComparisonRowKind = EAiComparisonRowKind;

  public state: EAiComparisonState = EAiComparisonState.GENERATING;

  public rows: IAiComparisonRow[] = [];

  public proposal: IAiAdjustmentResponse | null = null;

  public failure: EAiAdjustmentFailure | null = null;

  /** What the API said about the failure, beyond which screen to show */
  public failureDetails: IAiAdjustmentFailureDetails | null = null;

  public comparisonTab: TAiComparisonTab = 'new';

  /** True while the discard call is in flight, so it cannot be fired twice */
  public discarding = false;

  public showMobileLayout$: Observable<boolean>;

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: IAiComparisonDialogData,
    private dialogRef: MatDialogRef<AiAdjustmentComparisonDialogComponent, IAiComparisonDialogResult>,
    private aiAdjustmentService: AiAdjustmentService,
    private $breakpoint: BreakpointsProvider,
    private cdr: ChangeDetectorRef,
  ) {
    this.showMobileLayout$ = this.$breakpoint.isMobile$;
  }

  public ngOnInit(): void {
    // The old column is renderable immediately; only the new column waits.
    //
    this.rows = buildComparisonRows(this.data.activity, this.data.sections, this.emptyProposal());

    this.request();
  }

  /** The failure copy to show, as a translation key. */
  public get failureMessageKey(): string {
    switch (this.failure) {
      case EAiAdjustmentFailure.GUARDRAIL:
        // The guardrail names the input it refused, and which one it was
        // decides what the reader can do about it: rewrite their own
        // instruction, or take it up with whoever maintains the vision.
        //
        switch (this.failureDetails?.deniedInput) {
          case 'instruction':
            return 'activity.ai_adjustment.failure.guardrail_instruction';
          case 'vision':
            return 'activity.ai_adjustment.failure.guardrail_vision';
          case 'activity':
            return 'activity.ai_adjustment.failure.guardrail_activity';
          default:
            return 'activity.ai_adjustment.failure.guardrail';
        }
      case EAiAdjustmentFailure.BUDGET:
        return 'activity.ai_adjustment.failure.budget';
      case EAiAdjustmentFailure.UNAVAILABLE:
        return 'activity.ai_adjustment.failure.unavailable';
      case EAiAdjustmentFailure.NOT_ENABLED:
        return 'activity.ai_adjustment.failure.not_enabled';
      case EAiAdjustmentFailure.ACTIVITY_GONE:
        return 'activity.ai_adjustment.failure.activity_gone';
      case EAiAdjustmentFailure.INVALID_REQUEST:
        return 'activity.ai_adjustment.failure.invalid_request';
      default:
        return 'activity.ai_adjustment.failure.provider';
    }
  }

  /** Only a transient failure is worth inviting a retry for. */
  public get failureIsRetryable(): boolean {
    return this.failure === EAiAdjustmentFailure.PROVIDER;
  }

  /**
   * Why the guardrail refused, as a translation key.
   *
   * Kept in the same order as content-ai-api's DENY_CATEGORIES, which asks
   * callers to do exactly that so the two lists can be checked by eye. Null
   * when the guardrail named no reason this platform knows, in which case the
   * screen says only which input was refused.
   */
  public get failureCategoryKey(): string | null {
    switch (this.failureDetails?.category) {
      case 'explicit_content':
        return 'activity.ai_adjustment.failure.category.explicit_content';
      case 'harmful_or_unsafe_for_children':
        return 'activity.ai_adjustment.failure.category.harmful_or_unsafe_for_children';
      case 'hate_or_discrimination':
        return 'activity.ai_adjustment.failure.category.hate_or_discrimination';
      case 'off_topic':
        return 'activity.ai_adjustment.failure.category.off_topic';
      case 'prompt_injection':
        return 'activity.ai_adjustment.failure.category.prompt_injection';
      case 'personal_data':
        return 'activity.ai_adjustment.failure.category.personal_data';
      default:
        return null;
    }
  }

  /**
   * The ledger row the refused attempt was written as.
   *
   * Shown so support can find the attempt: nothing else on this screen
   * identifies it, and the ledger is where the spend is recorded.
   */
  public get failureReference(): number | null {
    return this.failureDetails?.aiAdjustmentId ?? null;
  }

  public get changedCount(): number {
    return this.rows.filter((row) => row.changed).length;
  }

  public setComparisonTab(tab: TAiComparisonTab): void {
    this.comparisonTab = tab;
  }

  public acceptUpdate(): void {
    // `discarding` too: the dialog stays open and interactive for the whole
    // reject round trip, and MatDialogRef.close() overwrites an earlier result
    // rather than refusing, so an accept landing in that window is either
    // silently replaced by the discard or rejected by the API as a spent
    // adjustment. Either way the user's accept is lost.
    //
    if (isNil(this.proposal) || this.discarding) {
      return;
    }

    this.dialogRef.close({ decision: 'accept-update', proposal: this.proposal });
  }

  public acceptCopy(): void {
    // `discarding` too: the dialog stays open and interactive for the whole
    // reject round trip, and MatDialogRef.close() overwrites an earlier result
    // rather than refusing, so an accept landing in that window is either
    // silently replaced by the discard or rejected by the API as a spent
    // adjustment. Either way the user's accept is lost.
    //
    if (isNil(this.proposal) || this.discarding) {
      return;
    }

    this.dialogRef.close({ decision: 'accept-copy', proposal: this.proposal });
  }

  /**
   * Records the dismissal and closes.
   *
   * Only a proposal the user actually saw is reported: a denied or failed
   * attempt is already ledgered server side, and reporting it again would
   * overwrite the real outcome with a rejection.
   */
  public async discard(): Promise<void> {
    if (this.discarding) {
      return;
    }

    if (isNil(this.proposal)) {
      this.dialogRef.close({ decision: this.state === EAiComparisonState.FAILED ? 'failed' : 'discard' });
      return;
    }

    this.discarding = true;
    this.cdr.markForCheck();

    try {
      await this.aiAdjustmentService.reject(this.proposal.ai_adjustment_id);
    } catch {
      // The dismissal is a bookkeeping detail and the endpoint is idempotent,
      // so a failure here must not trap the user in the dialog.
      //
    }

    this.dialogRef.close({ decision: 'discard', proposal: this.proposal });
  }

  /** Closes a failed attempt. Nothing was written, and nothing is reported. */
  public closeFailure(): void {
    this.dialogRef.close({ decision: 'failed' });
  }

  private async request(): Promise<void> {
    try {
      const proposal = await this.aiAdjustmentService.adjust(this.data.request);

      this.proposal = proposal;
      this.rows = buildComparisonRows(this.data.activity, this.data.sections, proposal);
      this.state = EAiComparisonState.COMPARING;
    } catch (error) {
      this.failureDetails = describeAdjustmentFailure(error);
      this.failure = this.failureDetails.failure;
      this.state = EAiComparisonState.FAILED;
    }

    // The request resolves outside Angular's change detection for an OnPush
    // component, so the state swap has to be announced.
    //
    this.cdr.markForCheck();
  }

  /**
   * A stand-in proposal that changes nothing, so the generating state can reuse
   * the same row builder and the same template as the comparison.
   *
   * The current sections are echoed back as untouched rather than left out: the
   * old column has to be complete while the new one is still spinning, and an
   * empty section list would make every section row appear only once the
   * response lands.
   */
  private emptyProposal(): IAiAdjustmentResponse {
    return {
      name: this.data.activity?.name,
      subtitle: this.data.activity?.subtitle ?? null,
      summary: this.data.activity?.summary ?? null,
      ai_adjustment_id: null as unknown as number,
      sections: (this.data.sections ?? []).map((section, index) => ({
        id: section.id,
        type: section.name,
        type_id: section.type_id,
        data: (section.data ?? {}) as Record<string, unknown>,
        order: section.order ?? index + 1,
        _action: 'none' as const,
        _index: index,
      })),
    };
  }

  public trackByRowKey(_index: number, row: IAiComparisonRow): string {
    return row.key;
  }
}
